import { Children, cloneElement, createContext, isValidElement, useContext, type ReactElement, type ReactNode } from 'react';
import type { CaixaDaPagina, Encaixe, No } from './documento-do-site';

/**
 * As caixas das páginas de fluxo (pagamento, perfil, carrinho, fórum...).
 *
 * Location: src/construtor
 * Why: essas páginas são as de sempre da loja, com a lógica delas. Cada caixa
 * (cartão, lista, formulário, painel) vem embrulhada em `<Caixa id="...">`, e
 * no layout Personalizado o lojista personaliza a caixa pelo id: estilo,
 * esconder e blocos nos encaixes (antes, início, fim e depois), guardados em
 * `PaginaDoSite.caixas`. Fora do Personalizado não há contexto e a caixa
 * devolve o filho como veio: as outras lojas não mudam em nada. Este arquivo
 * não carrega o construtor: quem desenha os blocos é a moldura (`desenhar`).
 */

interface CaixasDaPagina {
  caixas: Record<string, CaixaDaPagina> | undefined;
  /** Desenha os blocos de um encaixe (o `Nos` do Renderizador). */
  desenhar: (nos: No[]) => ReactNode;
}

const CaixasContext = createContext<CaixasDaPagina | null>(null);

export const CaixasDaPaginaProvider = CaixasContext.Provider;

/**
 * Marca uma caixa da página. O filho tem de ser um elemento que passa
 * `data-*` e `children` para o HTML. Com `miolo` (um cartão: `Card` com
 * `CardHeader` e `CardContent`), os blocos de dentro entram no último filho
 * dele, que é onde fica o espaçamento. Com `repetida` (cada item de uma
 * lista: pedido, tópico, presente), a caixa só recebe estilo, que vale para
 * todos os itens; blocos não entram nela, senão apareceriam em cada item.
 * Com `envolver` (componente que não passa `data-*` para o HTML), a caixa é
 * uma `div` em volta dele, só no Personalizado.
 */
export function Caixa({ id, miolo, repetida, envolver, children }: { id: string; miolo?: boolean; repetida?: boolean; envolver?: boolean; children: ReactElement }) {
  const ctx = useContext(CaixasContext);
  if (!ctx || !isValidElement(children)) return children;
  if (envolver) children = <div className="ct-caixa-envolta">{children}</div>;
  if (repetida) return cloneElement(children, { 'data-caixa': id, 'data-caixa-lista': '' } as Record<string, string>);
  const cx = ctx.caixas ? ctx.caixas[id] : undefined;
  const encaixe = (onde: Encaixe) => {
    const nos = cx ? cx[onde] : undefined;
    if (!nos || !nos.length) return null;
    return (
      <div className="ct-encaixe" data-regiao="caixa" data-encaixe={`${id}:${onde}`} data-onde={onde}>
        {ctx.desenhar(nos)}
      </div>
    );
  };
  const marca = { 'data-caixa': id } as Record<string, string>;
  const inicio = encaixe('inicio');
  const fim = encaixe('fim');
  const dentro = (children.props as { children?: ReactNode }).children;
  let el: ReactElement;
  if (!inicio && !fim) el = cloneElement(children, marca);
  else if (miolo) {
    // Os blocos de dentro vão para o último filho (o conteúdo do cartão).
    const filhos = Children.toArray(dentro);
    let ultimo = -1;
    filhos.forEach((f, i) => {
      if (isValidElement(f)) ultimo = i;
    });
    if (ultimo < 0) el = cloneElement(children, marca, inicio, dentro, fim);
    else {
      const alvo = filhos[ultimo] as ReactElement;
      filhos[ultimo] = cloneElement(alvo, undefined, inicio, (alvo.props as { children?: ReactNode }).children, fim);
      el = cloneElement(children, marca, ...filhos);
    }
  } else el = cloneElement(children, marca, inicio, dentro, fim);
  const antes = encaixe('antes');
  const depois = encaixe('depois');
  if (!antes && !depois) return el;
  return (
    <>
      {antes}
      {el}
      {depois}
    </>
  );
}
